<script setup lang="ts">
import { ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import type { FilterOption } from '../api/orderContracts'

export type SortOption = string

interface Props {
  sortOptions: FilterOption[]
  initialSort?: SortOption
}

const props = withDefaults(defineProps<Props>(), {
  sortOptions: () => [],
  initialSort: '',
})

const emit = defineEmits<{
  (e: 'apply', sort: SortOption): void
  (e: 'close'): void
}>()

const selected = ref<SortOption>(props.initialSort || props.sortOptions[0]?.value || '')
</script>

<template>
  <Box direction="col" class="osp">
    <Text type="heading" weight="medium" class="osp__title">Sort</Text>
    <Box class="osp__divider" />

    <Box direction="col" gap="4" class="osp__options">
      <Box
        v-for="opt in sortOptions"
        :key="opt.value"
        is="button"
        align="center"
        justify="between"
        class="osp__option"
        type="button"
        @click="selected = opt.value"
      >
        <Text type="body" is="span">{{ opt.label }}</Text>
        <Box class="osp__radio" :class="{ 'osp__radio--checked': selected === opt.value }" />
      </Box>
    </Box>

    <Box is="button" class="osp__apply" type="button" @click="emit('apply', selected)">
      <Text type="body" weight="semibold" color="inverse" is="span">Apply Sort</Text>
    </Box>
  </Box>
</template>

<style scoped>
.osp {
  width: 280px;
  background: var(--p-surface-50);
  border: 1px solid var(--p-primary-200);
  border-radius: 12px;
  padding: 1.25rem;
  box-sizing: border-box;
  box-shadow: 0 8px 24px rgba(10, 13, 18, 0.12);
}

.osp__title {
  display: block;
  margin-bottom: 0.75rem;
}

.osp__divider {
  height: 1px;
  background: var(--p-surface-100);
  margin-bottom: 1rem;
}

.osp__options {
  margin-bottom: 1.25rem;
}

.osp__option {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  width: 100%;
}

.osp__radio {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid var(--p-surface-300);
  flex-shrink: 0;
  box-sizing: border-box;
  transition:
    border-color 0.12s,
    background 0.12s;
  position: relative;
}

.osp__radio--checked {
  border-color: var(--p-primary-600);
  background: var(--p-primary-600);
}

.osp__radio--checked::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--p-surface-0);
}

.osp__apply {
  width: 100%;
  background: var(--p-primary-600);
  border: 1px solid var(--p-primary-600);
  border-radius: 10px;
  padding: 10px 16px;
  cursor: pointer;
  line-height: 1.5;
  box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);
  transition: background 0.15s;
}

.osp__apply:hover {
  background: var(--p-primary-700);
  border-color: var(--p-primary-700);
}
</style>
